// Przedmiar / LV (Leistungsverzeichnis) z Excela albo CSV (kolejka 13.10, poz. 4): pozycja (OZ), krótki tekst, jednostka, ilość.
// Pozycje obmiaru (kolumny arkusza Aufmaß) przypisuje się do numerów LV, a paczka obmiaru dostaje zestawienie według LV
// (ilość z LV, ilość wykonana, różnica). GAEB X83/D83 w następnej kolejności. Plik LV zostaje na urządzeniu, jak każdy dokument budowy.
import { unzipSync, strFromU8 } from 'fflate';

// nagłówki kolumn spotykane w LV z programów kosztorysowych i od generalnych wykonawców (DE i PL)
const KOLUMNY = {
  oz: /^(oz|ordnungszahl|pos(ition)?\.?(\s*-?\s*nr\.?)?|pos\.?nr\.?|nr\.?|lp\.?|pozycja)$/i,
  tekst: /^(kurztext|kurz-?text|text|bezeichnung|leistung|leistungsbeschreibung|beschreibung|opis|nazwa)$/i,
  jedn: /^(einheit|me|eh|mengeneinheit|einh\.?|jedn(ostka)?\.?|j\.?m\.?)$/i,
  ilosc: /^(menge|lv-?menge|ilość|ilosc|ilość lv)$/i,
};

/** Liczba z LV: „1.234,50” (DE), „1234.5”, „1 234,5”. */
export function liczba(v) {
  if (typeof v === 'number') return v;
  let s = String(v ?? '').trim().replace(/[\s ]/g, ''); if (!s) return null;
  if (/,\d+$/.test(s)) s = s.replace(/\./g, '').replace(',', '.'); else s = s.replace(/,/g, '');
  const n = Number(s); return Number.isFinite(n) ? n : null;
}

/** CSV z Excela (średnik w wersji niemieckiej i polskiej, przecinek albo tabulator), z cudzysłowami. */
export function wierszeCsv(tekst) {
  tekst = tekst.replace(/^﻿/, ''); const pierwsza = tekst.split(/\r?\n/, 1)[0];
  const sep = ['\t', ';', ','].map((z) => [z, pierwsza.split(z).length]).sort((a, b) => b[1] - a[1])[0][0];
  const W = []; let w = [], pole = '', cudz = false;
  for (let i = 0; i < tekst.length; i++) {
    const c = tekst[i];
    if (cudz) { if (c === '"' && tekst[i + 1] === '"') { pole += '"'; i++; } else if (c === '"') cudz = false; else pole += c; continue; }
    if (c === '"') cudz = true; else if (c === sep) { w.push(pole); pole = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && tekst[i + 1] === '\n') i++; w.push(pole); W.push(w); w = []; pole = ''; }
    else pole += c;
  }
  if (pole || w.length) { w.push(pole); W.push(w); }
  return W;
}

const kolNumer = (L) => L.split('').reduce((a, c) => a * 26 + c.charCodeAt(0) - 64, 0) - 1;
/** Pierwszy arkusz pliku .xlsx jako tablica wierszy (teksty wspólne, teksty w komórce i liczby). */
export function wierszeXlsx(bajty) {
  const pliki = unzipSync(new Uint8Array(bajty));
  const xml = (n) => new DOMParser().parseFromString(strFromU8(pliki[n]), 'application/xml');
  const ss = pliki['xl/sharedStrings.xml'] ? [...xml('xl/sharedStrings.xml').getElementsByTagName('si')].map((si) => [...si.getElementsByTagName('t')].map((t) => t.textContent).join('')) : [];
  // pierwszy arkusz wg workbook.xml (nie zawsze sheet1.xml)
  let sciezka = 'xl/worksheets/sheet1.xml';
  try { const wb = xml('xl/workbook.xml'); const rid = wb.getElementsByTagName('sheet')[0]?.getAttribute('r:id');
    const rel = [...xml('xl/_rels/workbook.xml.rels').getElementsByTagName('Relationship')].find((r) => r.getAttribute('Id') === rid);
    if (rel) { const t = rel.getAttribute('Target').replace(/^\//, ''); sciezka = t.startsWith('xl/') ? t : `xl/${t}`; } } catch { /* zostaje sheet1 */ }
  if (!pliki[sciezka]) throw new Error('w pliku Excel nie ma arkusza');
  const W = [];
  for (const r of xml(sciezka).getElementsByTagName('row')) {
    const w = [];
    for (const c of r.getElementsByTagName('c')) {
      const i = kolNumer(c.getAttribute('r').replace(/\d+/, '')); const t = c.getAttribute('t'); const v = c.getElementsByTagName('v')[0]?.textContent;
      w[i] = t === 's' ? ss[+v] : t === 'inlineStr' ? [...c.getElementsByTagName('t')].map((x) => x.textContent).join('') : t === 'str' || t === 'b' ? v : v != null && v !== '' ? Number(v) : '';
    }
    W[+r.getAttribute('r') - 1] = Array.from(w, (x) => x ?? '');
  }
  return Array.from(W, (x) => x || []);
}

/** Pozycje LV z wierszy tabeli: szuka wiersza nagłówka (OZ, Kurztext, Einheit, Menge), dalej bierze wiersze z numerem i tekstem. */
export function pozycjeLV(W) {
  const nag = W.findIndex((w) => { const k = w.map((x) => String(x ?? '').trim()); return k.some((x) => KOLUMNY.oz.test(x)) && k.some((x) => KOLUMNY.tekst.test(x)) && k.some((x) => KOLUMNY.ilosc.test(x)); });
  if (nag < 0) throw new Error('nie znalazłem wiersza nagłówka z kolumnami: pozycja (OZ), krótki tekst, jednostka, ilość (Menge)');
  const k = W[nag].map((x) => String(x ?? '').trim()); const kol = Object.fromEntries(Object.entries(KOLUMNY).map(([n, re]) => [n, k.findIndex((x) => re.test(x))]));
  const pozycje = []; const widziane = new Set();
  for (const w of W.slice(nag + 1)) {
    const oz = String(w[kol.oz] ?? '').trim(), tekst = String(w[kol.tekst] ?? '').trim().replace(/\s+/g, ' ');
    const ilosc = liczba(w[kol.ilosc]);
    // tytuły (Titel, Los) bez ilości i jednostki pomijamy: do obmiaru przypisuje się tylko pozycje z ilością
    if (!oz || !tekst || ilosc == null || widziane.has(oz)) continue;
    widziane.add(oz); pozycje.push({ oz, tekst: tekst.slice(0, 200), jedn: kol.jedn >= 0 ? String(w[kol.jedn] ?? '').trim() : '', ilosc });
  }
  if (!pozycje.length) throw new Error('w tabeli LV nie ma pozycji z ilością');
  return pozycje;
}

/** GAEB DA XML (X83, X84, X86): pozycje z ilością; OZ = numery części (Titel, Untertitel, Position) połączone kropką. */
export function pozycjeGaeb(tekst) {
  const doc = new DOMParser().parseFromString(tekst.replace(/^\ufeff/, ''), 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new Error('plik GAEB XML jest uszkodzony');
  const dzieci = (el, n) => [...el.children].filter((c) => c.localName === n);
  const pierwszy = (el, ...sciezka) => sciezka.reduce((e, n) => e && dzieci(e, n)[0], el);
  const pozycje = [];
  for (const it of [...doc.getElementsByTagName('*')].filter((e) => e.localName === 'Item')) {
    const nr = []; for (let e = it; e; e = e.parentElement) if (e.getAttribute?.('RNoPart') != null && ['Item', 'BoQCtgy'].includes(e.localName)) nr.unshift(e.getAttribute('RNoPart').trim());
    const ilosc = liczba(pierwszy(it, 'Qty')?.textContent?.trim()); if (ilosc == null) continue; // pozycja bez ilości (np. Bedarfsposition bez Menge)
    const opis = pierwszy(it, 'Description', 'CompleteText');
    const tekst = (pierwszy(opis, 'OutlineText', 'OutlTxt', 'TextOutlTxt') || pierwszy(opis, 'DetailTxt', 'Text') || opis)?.textContent?.replace(/\s+/g, ' ').trim() || '';
    if (!tekst) continue;
    pozycje.push({ oz: nr.join('.'), tekst: tekst.slice(0, 200), jedn: pierwszy(it, 'QU')?.textContent?.trim() || '', ilosc });
  }
  if (!pozycje.length) throw new Error('w pliku GAEB nie ma pozycji z ilością');
  return pozycje;
}

/** GAEB 90 (D83): wiersze po 80 znaków; „21” = pozycja (OZ w kolumnach 3–11, ilość 11 cyfr z 3 miejscami po przecinku, jednostka),
 *  „25” = krótki tekst pozycji. Układ wg opisu formatu GAEB 90; do sprawdzenia na prawdziwym pliku z programu AVA. */
export function pozycjeGaeb90(tekst) {
  const pozycje = []; let akt = null;
  for (const w of tekst.split(/\r?\n/)) {
    const typ = w.slice(0, 2);
    if (typ === '21') {
      const oz = w.slice(2, 11).trim().replace(/\s+/g, '.'); const m = w.slice(11, 72).match(/(\d{11})\s*([A-Za-zÄÖÜäöüß%²³/.]{1,4})?/);
      akt = oz && m ? { oz, tekst: '', jedn: (m[2] || '').trim(), ilosc: +m[1] / 1000 } : null; if (akt) pozycje.push(akt);
    } else if (typ === '25' && akt) akt.tekst = `${akt.tekst} ${w.slice(2, 72).trim()}`.trim().slice(0, 200);
    else if (!['25', '26'].includes(typ)) akt = typ === '21' ? akt : null;
  }
  const L = pozycje.filter((p) => p.tekst); if (!L.length) throw new Error('w pliku GAEB 90 (D83) nie znalazłem pozycji z ilością; zapisz LV jako X83, Excel albo CSV');
  return L;
}

/** Tekst pliku z rozpoznaniem kodowania (przegląd 14.10): BOM UTF-8 / UTF-16 (eksporty niektórych mierników), deklaracja encoding w XML
 *  (GAEB DA XML bywa w ISO-8859-1), inaczej UTF-8, a gdy daje znaki zastępcze: Windows-1252 (CSV z niemieckiego Excela). */
export function tekstPliku(bajty) {
  const d = (k, b = bajty) => new TextDecoder(k).decode(b);
  if (bajty[0] === 0xff && bajty[1] === 0xfe) return d('utf-16le', bajty.subarray(2));
  if (bajty[0] === 0xfe && bajty[1] === 0xff) return d('utf-16be', bajty.subarray(2));
  if (bajty[0] === 0xef && bajty[1] === 0xbb && bajty[2] === 0xbf) return d('utf-8', bajty.subarray(3));
  const deklaracja = /^\s*<\?xml[^>]*encoding=["']([\w.-]+)["']/i.exec(d('latin1', bajty.subarray(0, 200)))?.[1];
  if (deklaracja) { try { return d(deklaracja.toLowerCase()); } catch { /* nieznane kodowanie: dalej jak zwykle */ } }
  const t = d('utf-8'); return t.includes('\ufffd') ? d('windows-1252') : t;
}

/** Plik LV (File / Blob z nazwą): .csv, .txt albo .xlsx. */
export async function czytajLV(plik) {
  const nazwa = plik.name || '';
  if (/\.xlsx$/i.test(nazwa) || plik.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return pozycjeLV(wierszeXlsx(await plik.arrayBuffer()));
  if (/\.x8[0-9]$/i.test(nazwa)) return pozycjeGaeb(tekstPliku(new Uint8Array(await plik.arrayBuffer())));
  if (/\.(d|p)8[0-9]$/i.test(nazwa)) return pozycjeGaeb90(new TextDecoder('windows-1252').decode(new Uint8Array(await plik.arrayBuffer())));
  if (/\.xls$/i.test(nazwa)) throw new Error('stary format .xls: zapisz plik w Excelu jako .xlsx albo CSV');
  return pozycjeLV(wierszeCsv(tekstPliku(new Uint8Array(await plik.arrayBuffer()))));
}

// ——— przypisanie pozycji obmiaru do LV ———
const SZT = /^(st|stk|stck|stück|szt|psch|pausch|stk\.|st\.)\.?$/i, MB = /^(m|lfm|lfdm|mb|lfd\.? ?m)\.?$/i;
const SZCZEGOLNE = ['bodentank', 'boden', 'daten', 'data', 'e30', 'ip44', 'ip54', 'ip65', 'feuchtraum', 'aufputz', 'herd', 'cee', 'usb', 'not', 'sicherheit', 'brandschutz', 'tief', 'elektronik'];
const slowa = (s) => String(s).toLowerCase().replace(/[()]/g, ' ').replace(/steckdose|sterckdose/g, 'steckdose').replace(/funktionserhalt/g, 'e30').split(/[^a-z0-9äöüß]+/).filter((x) => x.length >= 2 && !['in', 'mit', 'und', 'fach'].includes(x));

/** Najlepsza pozycja LV dla kolumny obmiaru (klucz = nagłówek, jedn = 'szt' albo 'm'); null, gdy nic nie pasuje. */
export function sugestiaLV(klucz, jedn, pozycje) {
  const S = slowa(klucz); if (!S.length) return null; let best = null;
  for (const p of pozycje) {
    // jednostka z LV musi pasować (sztuki do sztuk, metry do metrów); pusta jednostka nie wyklucza
    if (p.jedn && ((jedn === 'm' && SZT.test(p.jedn)) || (jedn === 'szt' && MB.test(p.jedn)))) continue;
    const T = slowa(p.tekst);
    // pozycja dla innego rodzaju osprzętu (puszka podłogowa, gniazdo danych, E30, IP44…) nie pasuje do zwykłej kolumny, choć ma wspólne słowo
    if (T.some((t) => SZCZEGOLNE.some((z) => t.startsWith(z)) && !S.some((s) => s.startsWith(t.slice(0, 4)) || t.startsWith(s)))) continue;
    const traf = S.filter((s) => T.some((t) => t === s || (s.length >= 4 && t.startsWith(s)) || (t.length >= 4 && s.startsWith(t)))).length;
    const wynik = traf / S.length + (traf ? 0.01 / (1 + T.length) : 0); // przy remisie krótszy tekst (bardziej ogólna pozycja)
    if (traf && (!best || wynik > best.wynik)) best = { oz: p.oz, wynik };
  }
  return best && best.wynik >= 0.5 ? best.oz : null;
}

/** Zestawienie według LV: ilości wykonane (suma kolumn obmiaru przypisanych do pozycji) obok ilości z LV. */
export function zestawienieLV(pozycje, przypisania, sumy) {
  const wg = {}; const bez = [];
  for (const [klucz, v] of Object.entries(sumy)) { const oz = przypisania[klucz]; if (oz && pozycje.some((p) => p.oz === oz)) (wg[oz] ||= { wykonano: 0, kolumny: [] }), wg[oz].wykonano = Math.round((wg[oz].wykonano + v) * 100) / 100, wg[oz].kolumny.push(klucz); else bez.push(klucz); }
  return { wiersze: pozycje.map((p) => ({ ...p, wykonano: wg[p.oz]?.wykonano || 0, kolumny: wg[p.oz]?.kolumny || [] })), bez };
}

/** CSV zestawienia do Excela (średnik, przecinek dziesiętny, BOM). */
export function csvZestawienia({ wiersze, bez }, tytul) {
  const l = (n) => String(n).replace('.', ',');
  const pole = (s) => (/[;"\n]/.test(String(s)) ? `"${String(s).replace(/"/g, '""')}"` : String(s));
  const L = [pole(tytul), '', 'OZ;Kurztext;Einheit;LV-Menge;Ausgeführt;Differenz;Spalten im Aufmaß'];
  for (const w of wiersze) L.push([w.oz, w.tekst, w.jedn, l(w.ilosc), l(w.wykonano), l(Math.round((w.wykonano - w.ilosc) * 100) / 100), w.kolumny.join(', ')].map(pole).join(';'));
  if (bez.length) L.push('', pole(`Ohne LV-Position: ${bez.join(', ')}`));
  return '﻿' + L.join('\r\n') + '\r\n';
}
